import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Tag, Collapse } from 'antd';
import { normalizeBase } from '@digiwedge/ui-health-panels';

type Json = Record<string, unknown>;

export interface RegistryFullDiagnosticsPanelProps {
  base: string;
}

export default function RegistryFullDiagnosticsPanel({ base }: RegistryFullDiagnosticsPanelProps) {
  const { Text } = Typography;
  const fullUrl = useMemo(() => (base ? `${normalizeBase(base)}/health/full` : ''), [base]);
  const fallbackUrl = useMemo(() => (base ? `${normalizeBase(base)}/health` : ''), [base]);
  const [data, setData] = useState<Json | null>(null);
  const [status, setStatus] = useState<'idle' | 'ok' | 'fail'>('idle');
  const [endpoint, setEndpoint] = useState<string>('');

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!fullUrl) return;
      try {
        let res = await fetch(fullUrl, { credentials: 'include' });
        let used = fullUrl;
        if (res.status === 404 || res.status === 501) {
          res = await fetch(fallbackUrl, { credentials: 'include' });
          used = fallbackUrl;
        }
        const text = await res.text();
        if (!alive) return;
        setEndpoint(used);
        setStatus(res.ok ? 'ok' : 'fail');
        setData(text ? (JSON.parse(text) as Json) : {});
      } catch {
        if (!alive) return;
        setEndpoint(fullUrl || fallbackUrl);
        setStatus('fail');
        setData(null);
      }
    }
    void run();
    return () => {
      alive = false;
    };
  }, [fullUrl, fallbackUrl]);

  const statusColor = status === 'ok' ? 'green' : status === 'fail' ? 'red' : 'default';

  return (
    <Card
      title="Full Health Diagnostics"
      extra={<Tag color={statusColor}>{status.toUpperCase()}</Tag>}
    >
      <Text type="secondary">Endpoint: </Text>
      <Text code>{endpoint || 'not set'}</Text>
      <Collapse
        style={{ marginTop: 8 }}
        size="small"
        items={[
          {
            key: '1',
            label: 'JSON',
            children: (
              <pre style={{ margin: 0, maxHeight: 360, overflow: 'auto' }}>
                {JSON.stringify(data ?? {}, null, 2)}
              </pre>
            ),
          },
        ]}
      />
    </Card>
  );
}
